import { getAPI, postAPI } from "../../../../api/frameworksApi";
import { sharedRequest } from "../../../../utils/sharedRequest";
import type { ApiEnvelope } from "../../../../api/frameworksApi";


const STOC_BASE_URL = (window.BE_URL || "") + "/stocApp/api/v1";
const SCAN_COVERAGE_BASE_URL = `${STOC_BASE_URL}/scan-coverage`;

function unwrapEnvelope<T>(response: unknown): T {
  const envelope = response as ApiEnvelope<T> | T;
  // Handle both envelope format and direct data format
  if (envelope && typeof envelope === "object" && "data" in envelope) {
    return (envelope as ApiEnvelope<T>).data;
  }
  return envelope as T;
}

export interface ScanCoverageFilterApplication {
  id: string;
  name: string;
}

export interface ScanCoverageFilterScanType {
  value: string;
  label: string;
}

export interface ScanCoverageFilters {
  applications: ScanCoverageFilterApplication[];
  tools: string[];
  scanTypes: ScanCoverageFilterScanType[];
  environments: string[];
  assetTypes: string[];
}

export const fetchScanCoverageFilters = async (): Promise<ScanCoverageFilters> => {
  const response = await getAPI(`${SCAN_COVERAGE_BASE_URL}/filters`);
  return unwrapEnvelope<ScanCoverageFilters>(response);
};

export interface ScanSearchRequest {
  search?: string;
  applicationNames?: string[];
  tools?: string[];
  scanPurposes?: string[];
  environments?: string[];
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export type ScanSearchStatus = "HEALTHY" | "STALE" | "FAILED" | "UNKNOWN";

export interface ScanSearchItem {
  id: string;
  scan: string;
  tool: string;
  type: string;
  scanPurpose?: string;
  status: ScanSearchStatus;
  coverage: string;
  passedAssets: number;
  failedAssets: number;
  lastScanCompleted: string | null;
}

export interface ScanSearchResponse {
  content: ScanSearchItem[];
  totalOrgAssets: number;
  catalogueSyncFailed: boolean;
  catalogueSyncMessage: string;
  catalogueSyncStatus: string;
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export const fetchScanCoverageScans = async (
  body: ScanSearchRequest,
): Promise<ScanSearchResponse> => {
  const response = await postAPI(`${SCAN_COVERAGE_BASE_URL}/scans/search`, body);
  return unwrapEnvelope<ScanSearchResponse>(response);
};

export interface ScanDetail {
  scanDefinitionId: string;
  id?: string;
  scan: string;
  tool: string;
  toolInstanceId?: string;
  moduleType?: string;
  scanPurpose?: string;
  scanTypeLabel?: string;
  scanType?: string;
  executionMethod?: string;
  status: ScanSearchStatus;
  scheduleFrequency?: string | null;
  scheduleTime?: string | null;
  scheduleExpression?: string | null;
  latestCompletedExecutionId?: string | null;
  lastScanCompleted?: string | null;
  lastDataSync?: string | null;
  sourcePresent?: boolean;
  lastDetectedAt?: string | null;
  cycleInProgress?: boolean;
  coverage?: string;
  passedAssets?: number;
  failedAssets?: number;
}

export const fetchScanCoverageScanDetail = async (scanDefinitionId: string): Promise<ScanDetail> => {
  const response = await getAPI(`${SCAN_COVERAGE_BASE_URL}/scans/${encodeURIComponent(scanDefinitionId)}`);
  const raw = unwrapEnvelope<ScanDetail>(response);
  const rawAny = raw as unknown as Record<string, unknown>;
  const purpose = (raw.scanPurpose || rawAny.purpose || rawAny.scan_purpose) as string | undefined;
  const result: ScanDetail = {
    ...raw,
    scanDefinitionId: raw.id ?? scanDefinitionId,
    scanPurpose: purpose,
    scanTypeLabel: raw.scanTypeLabel ?? raw.scanType ?? purpose,
  };
  return result;
};

export interface ScanAssetSearchRequest {
  assetSearch?: string;
  applicationNames?: string[];
  environments?: string[];
  failureReasons?: string[];
  results?: string[];
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export type ScanAssetResult = "SCANNED" | "FAILED" | "UNKNOWN" | "NOT_SCANNED";
export type ScanAssetCorrelationStatus = "MATCHED" | "UNMATCHED";

export interface ScanAssetSearchItem {
  id: string;
  asset: string;
  application: string;
  environment?: string;
  ipAddress: string | null;
  tool: string;
  scanType: string;
  scanId: string;
  lastScanCompleted: string | null;
  result: ScanAssetResult;
  failureReason?: string;
  applicableToScan: boolean;
  onboardedToTool: boolean;
  expectedInCycle: boolean;
  cmdbAssetId?: string;
}

export interface ScanAssetSearchResponse {
  content: ScanAssetSearchItem[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  cycleCompletedAt: string | null;
}

export const fetchScanCoverageAssets = async (
  scanDefinitionId: string,
  body: ScanAssetSearchRequest,
): Promise<ScanAssetSearchResponse> => {
  const response = await postAPI(`${SCAN_COVERAGE_BASE_URL}/scans/${encodeURIComponent(scanDefinitionId)}/assets/search`, body);
  return unwrapEnvelope<ScanAssetSearchResponse>(response);
};

export interface ScanAssetFilters {
  applications: string[];
  environments: string[];
  failureReasons: string[];
}

export const fetchScanCoverageAssetFilters = async (scanDefinitionId: string): Promise<ScanAssetFilters> => {
  const response = await getAPI(`${SCAN_COVERAGE_BASE_URL}/scans/${encodeURIComponent(scanDefinitionId)}/assets/filters`);
  return unwrapEnvelope<ScanAssetFilters>(response);
};

/* ==========================================================================
   Asset-Centric Scan Coverage Integration
   ========================================================================== */

export type ScanPurpose =
  | "VULNERABILITY"
  | "COMPLIANCE"
  | "CONFIGURATION"
  | "MALWARE"
  | "ENDPOINT"
  | "RECOMMENDATION";

export type AssetPurposeCoverageStatus =
  | "SUCCESS"
  | "FAILED"
  | "NOT_SCANNED"
  | "UNKNOWN"
  | "NEVER_SCANNED"
  | "NOT_CONFIGURED_IN_TOOL";

export type AssetPurposeCoverageSort =
  | "asset"
  | "ipAddress"
  | "environment"
  | "application"
  | "organization"
  | "lastSuccessfulScanAt"
  | "purposeStatus";

export interface PurposeStatusFilter {
  scanPurpose: ScanPurpose;
  statuses: AssetPurposeCoverageStatus[];
}

export interface AssetPurposeCoverageSearchRequest {
  search?: string | null;
  applicationNames?: string[] | null;
  environments?: string[] | null;
  organizations?: string[] | null;
  purposeFilters?: PurposeStatusFilter[] | null;
  /** 1-based. Defaults to 1. */
  page?: number | null;
  /** Defaults to 20. Valid range: 1-100. */
  pageSize?: number | null;
  sortBy?: AssetPurposeCoverageSort | null;
  /** Required for lastSuccessfulScanAt and purposeStatus sorting. */
  sortPurpose?: ScanPurpose | null;
  sortDir?: "asc" | "desc" | null;
  tools?: string[] | null;
}

export interface ApplicationMappingItem {
  applicationId: string;
  applicationName: string;
  organization: string | null;
}

export interface PurposeCoverageItem {
  scanPurpose: ScanPurpose;
  status: AssetPurposeCoverageStatus;
  lastSuccessfulScanAt: string | null;
  scanExecutionId: string | null;
  scanAssetResultId: string | null;
}

export interface AssetPurposeCoverageItem {
  assetId: string;
  assetName: string | null;
  hostname: string | null;
  ipAddress: string | null;
  assetType: string | null;
  environment: string;
  applicationMappings: ApplicationMappingItem[];
  purposes: PurposeCoverageItem[];
}

export interface AssetPurposeCoverageSearchResponse {
  content: AssetPurposeCoverageItem[];
  /** 0-based. */
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface AssetScanResultDetailResponse {
  assetId: string;
  scanAssetResultId: string;
  scanExecutionId: string;
  scanDefinitionId: string;
  scanName: string;
  scanPurpose: ScanPurpose;
  toolInstanceId: string;
  toolName: string;
  executionStatus: string;
  assetScanStatus: string;
  executionStartedAt: string | null;
  executionCompletedAt: string | null;
  assetScanStartedAt: string | null;
  assetScanCompletedAt: string | null;
  vendorStatus: string | null;
  reasonCode: string | null;
  reasonText: string | null;
}

export const fetchAssetScanCoverage = async (
  body: AssetPurposeCoverageSearchRequest,
): Promise<AssetPurposeCoverageSearchResponse> => {
  const response = await postAPI(`${SCAN_COVERAGE_BASE_URL}/assets/search`, body);
  return unwrapEnvelope<AssetPurposeCoverageSearchResponse>(response);
};

export const fetchAssetScanResultDetail = async (
  assetId: string,
  scanAssetResultId: string,
): Promise<AssetScanResultDetailResponse> => {
  const response = await getAPI(
    `${SCAN_COVERAGE_BASE_URL}/assets/${encodeURIComponent(assetId)}/scan-results/${encodeURIComponent(scanAssetResultId)}`,
  );
  return unwrapEnvelope<AssetScanResultDetailResponse>(response);
};

/* ==========================================================================
   Scan Coverage Dashboard Integration (Plan & Handover)
   ========================================================================== */

const SCAN_COVERAGE_DASHBOARD_BASE_URL = `${SCAN_COVERAGE_BASE_URL}/dashboard`;

export interface ScanDashboardFilterApp {
  id: string;
  name: string;
}

export interface ScanDashboardFilterOrg {
  id: string;
  name: string;
}

export interface ScanDashboardFilterTool {
  toolInstanceId: string;
  toolName: string;
  vendor: string;
}

export interface ScanDashboardFilterOptionsResponse {
  applications: ScanDashboardFilterApp[];
  organizations: ScanDashboardFilterOrg[];
  environments: string[];
  tools: ScanDashboardFilterTool[];
  scanPurposes: string[];
  vendorScanTypes: { value: string; label: string }[];
}

export interface ScanDashboardKpis {
  eligibleAssetCount: number;
  expectedAssetCount: number;
  scannedAssetCount: number;
  failedAssetCount: number;
  notScannedAssetCount: number;
  unknownAssetCount: number;
  expectedOpportunityCount: number;
  scannedOpportunityCount: number;
  failedOpportunityCount: number;
  notScannedOpportunityCount: number;
  unknownOpportunityCount: number;
  coveragePercentage: number | null;
  unobservedAssetCount: number;
}

export type ScanReportingMode =
  | "ASSET_RESULTS"
  | "LATEST_STATE_SNAPSHOT"
  | "UNSUPPORTED";

export interface ScanDashboardCoverageGroup {
  toolInstanceId: string;
  toolName: string;
  scanPurpose: string;
  reportingMode?: ScanReportingMode;
  scannedAssetCount?: number;
  expectedOpportunityCount: number;
  scannedOpportunityCount: number;
  failedOpportunityCount: number;
  notScannedOpportunityCount: number;
  unknownOpportunityCount: number;
  coveragePercentage: number | null;
  dataState: "AVAILABLE" | "PARTIAL" | "EMPTY" | "UNAVAILABLE";
}

export interface ScanDashboardFailureByApplication {
  applicationId: string;
  applicationName: string;
  totalAssets: number;
  failedAssetCount: number;
  failedPercentage?: string;
}

export interface ScanDashboardFailureByEnvironment {
  environment: string | null;
  totalAssets: number;
  failedAssetCount: number;
  failedPercentage?: string;
}

export interface ScanDashboardFailures {
  totalAssets: number;
  applications: ScanDashboardFailureByApplication[];
  environment: ScanDashboardFailureByEnvironment[];
}

export interface ScanDashboardCycleSummary {
  configuredScanCount: number;
  completedScanCount: number;
  runningScanCount: number;
  failedScanCount: number;
  unknownScanCount: number;
  neverRunScanCount: number;
}

export interface ScanDashboardWarning {
  code: string;
  message: string;
  toolInstanceId?: string | null;
  scanDefinitionId?: string | null;
}

export interface ScanDashboardOverviewResponse {
  asOf: string;
  dataState: "AVAILABLE" | "PARTIAL" | "EMPTY" | "UNAVAILABLE";
  warnings: ScanDashboardWarning[];
  kpis: ScanDashboardKpis;
  coverageByToolAndScanType: ScanDashboardCoverageGroup[];
  failuresByApplicationEnvironment: ScanDashboardFailures;
  cycleSummary: ScanDashboardCycleSummary;
}

export interface ScanDashboardCycleItem {
  scanDefinitionId: string;
  scanName: string;
  toolInstanceId: string;
  toolName: string;
  scanPurpose: string;
  latestExecutionId: string | null;
  coverageExecutionId: string | null;
  executionStatus: "COMPLETED" | "RUNNING" | "FAILED" | "SCHEDULED" | "CANCELLED" | "ABORTED" | "UNKNOWN" | null;
  ingestState: "COMPLETE" | "IN_PROGRESS" | "FAILED" | null;
  reportingMode: "ASSET_RESULTS" | "LATEST_STATE_SNAPSHOT" | "UNSUPPORTED" | null;
  freshnessState: "FRESH" | "STALE" | "FAILED" | "NEVER_RECEIVED";
  startedAt: string | null;
  completedAt: string | null;
  lastSuccessfulSyncAt: string | null;
  presentInSource: boolean;
  coveragePercentage: number | null;
  statusObservedAt: string | null;
}

export interface ScanDashboardApplicationMapping {
  applicationId: string;
  applicationName: string;
  organizationId?: string;
  organizationName?: string;
}

export interface ScanDashboardFailureItem {
  assetId: string;
  hostname: string;
  ipAddress: string;
  applicationMappings: ScanDashboardApplicationMapping[];
  environment: string | null;
  toolInstanceId: string;
  toolName: string;
  scanPurpose: string;
  scanDefinitionId: string;
  scanExecutionId: string;
  scanAssetResultId: string;
  assetScanStatus: string;
  reasonCode: string | null;
  reasonText: string | null;
  scanCompletedAt: string;
  freshnessState: "FRESH" | "STALE" | "FAILED" | "NEVER_RECEIVED";
}

export interface ScanDashboardFilterRequest {
  applicationIds?: string[];
  organizationIds?: string[];
  environments?: string[];
  includeUnknownEnvironment?: boolean;
  toolInstanceIds?: string[];
  scanPurposes?: string[];
  vendorScanTypes?: string[];
  asOf?: string | null;
}

export interface ScanDashboardCycleSearchRequest extends ScanDashboardFilterRequest {
  page?: number; // 1-based in request
  pageSize?: number;
  sortBy?: "SCAN_NAME" | "COMPLETED_AT" | "EXECUTION_STATUS";
  sortDirection?: "ASC" | "DESC";
  executionStatuses?: string[];
}

export interface ScanDashboardCycleSearchResponse {
  items: ScanDashboardCycleItem[];
  page: number; // 0-based in response
  pageSize: number;
  totalElements: number;
  totalPages: number;
  asOf: string;
}

export interface ScanDashboardFailureSearchRequest extends ScanDashboardFilterRequest {
  page?: number; // 1-based in request
  pageSize?: number;
  search?: string;
  reasonCodes?: string[];
  sortBy?: "HOSTNAME" | "APPLICATION" | "ENVIRONMENT" | "COMPLETED_AT";
  sortDirection?: "ASC" | "DESC";
}

export interface ScanDashboardFailureSearchResponse {
  items: ScanDashboardFailureItem[];
  page: number; // 0-based in response
  pageSize: number;
  totalElements: number;
  totalPages: number;
  asOf: string;
}

export const fetchScanDashboardFilterOptions = async (): Promise<ScanDashboardFilterOptionsResponse> => {
  // The page shell and each tab load these options on mount; share one call between them.
  const response = await sharedRequest("scan-dashboard-filters", () =>
    getAPI(`${SCAN_COVERAGE_DASHBOARD_BASE_URL}/filters`),
  );
  return unwrapEnvelope<ScanDashboardFilterOptionsResponse>(response);
};

export interface ScanPurposesResponse {
  toolInstanceIds: string[];
  scanPurposes: string[];
}

// Scan purposes available for the given tool instances (ids come from the tool filter).
export const fetchScanPurposes = async (toolInstanceIds: string[]): Promise<ScanPurposesResponse> => {
  const response = await postAPI(`${SCAN_COVERAGE_DASHBOARD_BASE_URL}/scan-purposes`, { toolInstanceIds });
  return unwrapEnvelope<ScanPurposesResponse>(response);
};

export const fetchScanDashboardOverview = async (
  body: ScanDashboardFilterRequest,
): Promise<ScanDashboardOverviewResponse> => {
  const response = await postAPI(`${SCAN_COVERAGE_DASHBOARD_BASE_URL}/overview`, body);
  return unwrapEnvelope<ScanDashboardOverviewResponse>(response);
};

export const fetchScanDashboardCycles = async (
  body: ScanDashboardCycleSearchRequest,
): Promise<ScanDashboardCycleSearchResponse> => {
  const response = await postAPI(`${SCAN_COVERAGE_DASHBOARD_BASE_URL}/cycles/search`, body);
  return unwrapEnvelope<ScanDashboardCycleSearchResponse>(response);
};

export const fetchScanDashboardFailures = async (
  body: ScanDashboardFailureSearchRequest,
): Promise<ScanDashboardFailureSearchResponse> => {
  const response = await postAPI(`${SCAN_COVERAGE_DASHBOARD_BASE_URL}/failures/search`, body);
  return unwrapEnvelope<ScanDashboardFailureSearchResponse>(response);
};

export const fetchScanDashboardEvidenceDetail = async (
  assetId: string,
  scanAssetResultId: string,
): Promise<AssetScanResultDetailResponse> => {
  const response = await getAPI(
    `${SCAN_COVERAGE_DASHBOARD_BASE_URL}/assets/${encodeURIComponent(assetId)}/scan-results/${encodeURIComponent(scanAssetResultId)}`,
  );
  return unwrapEnvelope<AssetScanResultDetailResponse>(response);
};


